/**
 * Sidebar Preferences Utility
 *
 * Manages per-sport sidebar customization state:
 * - Hidden items
 * - Section order
 * - Item order + item placement across sections
 */

const LEGACY_STORAGE_KEY = 'slam-sidebar-preferences';
const STORAGE_KEY_PREFIX = 'slam-sidebar-preferences-v2';
const PREFS_VERSION = 2;

const DEFAULT_SECTION_ORDER = [
  'playerAcquisition',
  'playerDevelopment',
  'scouting',
  'rosterManagement',
  'gettingStarted',
  'system',
];

function dedupe(values) {
  if (!Array.isArray(values)) return [];
  return [...new Set(values.filter((value) => typeof value === 'string' && value.length > 0))];
}

function getStorageKey(sport = 'global') {
  return `${STORAGE_KEY_PREFIX}:${sport}`;
}

function buildSectionItemsMap(navSections = []) {
  if (!Array.isArray(navSections) || navSections.length === 0) {
    return {};
  }

  return navSections.reduce((acc, section) => {
    const sectionId = section?.id;
    if (!sectionId) return acc;

    acc[sectionId] = Array.isArray(section?.items)
      ? section.items.map((item) => item.id).filter(Boolean)
      : [];

    return acc;
  }, {});
}

function getKnownItemIds(sectionItemsMap = {}) {
  return [...new Set(Object.values(sectionItemsMap).flat())];
}

function getDefaultSectionByItem(sectionItemsMap = {}) {
  return Object.entries(sectionItemsMap).reduce((acc, [sectionId, itemIds]) => {
    itemIds.forEach((itemId) => {
      if (!acc[itemId]) {
        acc[itemId] = sectionId;
      }
    });
    return acc;
  }, {});
}

function getDefaultSectionOrder(navSections = []) {
  const sectionIds = Array.isArray(navSections)
    ? navSections.map((section) => section?.id).filter(Boolean)
    : [];

  return sectionIds.length > 0 ? sectionIds : DEFAULT_SECTION_ORDER;
}

function createDefaultPreferences(navSections = []) {
  const sectionItemsMap = buildSectionItemsMap(navSections);
  const sectionOrder = getDefaultSectionOrder(navSections);

  const itemOrder = Object.keys(sectionItemsMap).reduce((acc, sectionId) => {
    acc[sectionId] = [...sectionItemsMap[sectionId]];
    return acc;
  }, {});

  return {
    version: PREFS_VERSION,
    hiddenItems: [],
    sectionOrder,
    itemOrder,
    updatedAt: null,
  };
}

function normalizeSectionOrder(rawOrder, validSectionIds) {
  const cleaned = dedupe(rawOrder);

  if (!Array.isArray(validSectionIds) || validSectionIds.length === 0) {
    return cleaned.length > 0 ? cleaned : [...DEFAULT_SECTION_ORDER];
  }

  const filtered = cleaned.filter((sectionId) => validSectionIds.includes(sectionId));

  validSectionIds.forEach((sectionId) => {
    if (!filtered.includes(sectionId)) {
      filtered.push(sectionId);
    }
  });

  return filtered;
}

function normalizeItemOrder(rawItemOrder, sectionItemsMap) {
  const sectionIds = Object.keys(sectionItemsMap);
  if (sectionIds.length === 0) {
    return {};
  }

  const knownItemIds = getKnownItemIds(sectionItemsMap);
  const knownItemSet = new Set(knownItemIds);
  const defaultSectionByItem = getDefaultSectionByItem(sectionItemsMap);

  const normalized = sectionIds.reduce((acc, sectionId) => {
    acc[sectionId] = [];
    return acc;
  }, {});

  const assignedItems = new Set();

  // Keep saved ordering while allowing items to move across sections.
  sectionIds.forEach((sectionId) => {
    const savedOrder = Array.isArray(rawItemOrder?.[sectionId]) ? rawItemOrder[sectionId] : [];
    dedupe(savedOrder).forEach((itemId) => {
      if (!knownItemSet.has(itemId) || assignedItems.has(itemId)) {
        return;
      }
      normalized[sectionId].push(itemId);
      assignedItems.add(itemId);
    });
  });

  // Ensure every known item is assigned to exactly one section.
  knownItemIds.forEach((itemId) => {
    if (assignedItems.has(itemId)) {
      return;
    }
    const fallbackSection = defaultSectionByItem[itemId] || sectionIds[0];
    normalized[fallbackSection].push(itemId);
    assignedItems.add(itemId);
  });

  return normalized;
}

function normalizeLooseItemOrder(rawItemOrder) {
  if (!rawItemOrder || typeof rawItemOrder !== 'object') {
    return {};
  }

  return Object.entries(rawItemOrder).reduce((acc, [sectionId, itemIds]) => {
    if (!sectionId) {
      return acc;
    }

    acc[sectionId] = dedupe(itemIds);
    return acc;
  }, {});
}

function sanitizePreferences(rawPreferences, navSections = []) {
  const defaults = createDefaultPreferences(navSections);
  if (!rawPreferences || typeof rawPreferences !== 'object') {
    return defaults;
  }

  const sectionItemsMap = buildSectionItemsMap(navSections);
  const hasSectionSchema = Object.keys(sectionItemsMap).length > 0;
  const validSectionIds = hasSectionSchema ? Object.keys(sectionItemsMap) : [];
  const validItemIds = hasSectionSchema ? Object.values(sectionItemsMap).flat() : [];

  return {
    version: PREFS_VERSION,
    hiddenItems: dedupe(rawPreferences.hiddenItems).filter((itemId) => (
      validItemIds.length === 0 ? true : validItemIds.includes(itemId)
    )),
    sectionOrder: normalizeSectionOrder(rawPreferences.sectionOrder, validSectionIds),
    itemOrder: hasSectionSchema
      ? normalizeItemOrder(rawPreferences.itemOrder, sectionItemsMap)
      : normalizeLooseItemOrder(rawPreferences.itemOrder),
    updatedAt: rawPreferences.updatedAt || null,
  };
}

function dispatchPreferenceChange(sport, preferences) {
  window.dispatchEvent(
    new CustomEvent('sidebarPreferencesChanged', {
      detail: { sport, preferences },
    })
  );
}

/**
 * Get current sidebar preferences from localStorage
 * @param {string} sport - Sport key (e.g. basketball, baseball)
 * @param {Array} navSections - Current sport nav sections for validation/defaults
 * @returns {Object}
 */
export function getSidebarPreferences(sport = 'global', navSections = []) {
  const storageKey = getStorageKey(sport);

  try {
    const stored = localStorage.getItem(storageKey);
    const legacy = !stored ? localStorage.getItem(LEGACY_STORAGE_KEY) : null;
    const source = stored || legacy;

    if (source) {
      const parsed = JSON.parse(source);
      const sanitized = sanitizePreferences(parsed, navSections);

      if (!stored) {
        localStorage.setItem(storageKey, JSON.stringify(sanitized));
      }

      return sanitized;
    }
  } catch (error) {
    console.warn('[SidebarPreferences] Failed to parse stored preferences:', error);
  }

  return createDefaultPreferences(navSections);
}

/**
 * Save sidebar preferences
 * @param {Object} preferences
 * @param {string} sport
 * @param {Array} navSections
 * @returns {Object|null}
 */
export function saveSidebarPreferences(preferences, sport = 'global', navSections = []) {
  const storageKey = getStorageKey(sport);

  try {
    const current = getSidebarPreferences(sport, navSections);
    const merged = {
      ...current,
      ...preferences,
      updatedAt: new Date().toISOString(),
    };
    const sanitized = sanitizePreferences(merged, navSections);

    localStorage.setItem(storageKey, JSON.stringify(sanitized));
    dispatchPreferenceChange(sport, sanitized);

    return sanitized;
  } catch (error) {
    console.error('[SidebarPreferences] Failed to save preferences:', error);
    return null;
  }
}

/**
 * Reset sport-specific preferences to defaults
 * @param {string} sport
 * @param {Array} navSections
 */
export function resetSidebarPreferences(sport = 'global', navSections = []) {
  const storageKey = getStorageKey(sport);

  try {
    localStorage.removeItem(storageKey);
    const defaults = createDefaultPreferences(navSections);
    dispatchPreferenceChange(sport, defaults);
    return defaults;
  } catch (error) {
    console.error('[SidebarPreferences] Failed to reset preferences:', error);
    return null;
  }
}

/**
 * Toggle visibility of a nav item
 */
export function toggleItemVisibility(itemId, sport = 'global', navSections = []) {
  const prefs = getSidebarPreferences(sport, navSections);
  const isHidden = prefs.hiddenItems.includes(itemId);
  const hiddenItems = isHidden
    ? prefs.hiddenItems.filter((id) => id !== itemId)
    : [...prefs.hiddenItems, itemId];

  saveSidebarPreferences({ ...prefs, hiddenItems }, sport, navSections);
  return !isHidden;
}

/**
 * Update section order
 */
export function updateSectionOrder(newOrder, sport = 'global', navSections = []) {
  const prefs = getSidebarPreferences(sport, navSections);
  return saveSidebarPreferences({ ...prefs, sectionOrder: newOrder }, sport, navSections);
}

/**
 * Update item order within a section
 */
export function updateItemOrder(sectionId, newOrder, sport = 'global', navSections = []) {
  const prefs = getSidebarPreferences(sport, navSections);
  const itemOrder = {
    ...(prefs.itemOrder || {}),
    [sectionId]: newOrder,
  };

  return saveSidebarPreferences({ ...prefs, itemOrder }, sport, navSections);
}

/**
 * Check if an item is hidden
 */
export function isItemHidden(itemId, sport = 'global', navSections = []) {
  const prefs = getSidebarPreferences(sport, navSections);
  return prefs.hiddenItems.includes(itemId);
}

/**
 * Get section order
 */
export function getSectionOrder(sport = 'global', navSections = []) {
  return getSidebarPreferences(sport, navSections).sectionOrder;
}

export { DEFAULT_SECTION_ORDER };
